/**
 * AuditIQ Sports — Demo Intelligence Suite
 * =========================================
 * Three pages exported from this file:
 *   - DemoIntelligenceDashboard   (/demo)            8 panels + filter bar + search
 *   - AthleteProfile               (/athlete/:id)    drill-down per spec §8
 *   - SportsDemoAdmin              (/admin/demo)     spec §15 controls
 *
 * Brand applied LOCALLY (Cinzel/Merriweather + #0d1f3c/#c9a84c/#f4efe6/#112244)
 * via .auditiq-demo wrapper class — does NOT touch global site CSS.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Toaster } from "../../components/ui/sonner";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api } from "../../lib/api";
import {
  Activity, AlertTriangle, ArrowRight, Award, Bell, Clipboard, Database, Download,
  Eye, FileText, Flame, Gauge, Heart, Loader2, Pause, Play, RefreshCw, Search,
  ScanLine, ShieldCheck, Stethoscope, Target, TrendingUp, Trophy, Users2, X,
} from "lucide-react";

// ---------- Brand wrapper styles (scoped) ----------
const BRAND_STYLES = `
.auditiq-demo { background: #0d1f3c; color: #f4efe6; min-height: 100vh; padding-bottom: 64px;
  font-family: 'Merriweather', Georgia, serif; }
.auditiq-demo .ai-h1, .auditiq-demo .ai-h2, .auditiq-demo .ai-h3 {
  font-family: 'Cinzel', 'Trajan Pro', serif; letter-spacing: 0.04em; color: #c9a84c; }
.auditiq-demo .ai-h1 { font-size: clamp(28px, 4vw, 44px); font-weight: 700; }
.auditiq-demo .ai-h2 { font-size: clamp(20px, 2.4vw, 28px); font-weight: 700; }
.auditiq-demo .ai-h3 { font-size: 18px; font-weight: 600; }
.auditiq-demo .ai-card { background: #112244; border: 1px solid rgba(201, 168, 76, 0.45);
  box-shadow: inset 0 0 0 2px #112244, 0 0 0 1px rgba(201,168,76,0.65);
  border-radius: 4px; padding: 18px; }
.auditiq-demo .ai-stat-num { font-family: 'Cinzel', serif; color: #c9a84c; font-size: 30px; font-weight: 700; }
.auditiq-demo .ai-stat-label { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: #f4efe6aa; }
.auditiq-demo .ai-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.auditiq-demo .ai-table th { color: #c9a84c; font-family: 'Cinzel', serif; font-weight: 700; text-align: left;
  padding: 10px 12px; border-bottom: 1px solid rgba(201,168,76,0.4); font-size: 12px; letter-spacing: .14em; }
.auditiq-demo .ai-table td { padding: 10px 12px; border-bottom: 1px solid rgba(244,239,230,0.08); color: #f4efe6; }
.auditiq-demo .ai-pill { display: inline-flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: 999px;
  font-size: 11px; font-weight: 600; }
.auditiq-demo .ai-banner { background: linear-gradient(90deg, #c9a84c22, #c9a84c11); border: 1px solid #c9a84c66;
  color: #f4efe6; padding: 10px 16px; border-radius: 4px; }
.auditiq-demo .ai-btn-gold { background: #c9a84c; color: #0d1f3c; font-weight: 700; padding: 8px 16px; border-radius: 3px;
  font-family: 'Cinzel', serif; letter-spacing: 0.08em; text-transform: uppercase; font-size: 12px; cursor: pointer; }
.auditiq-demo .ai-btn-gold:hover { background: #d4b558; }
.auditiq-demo .ai-btn-ghost { background: transparent; color: #c9a84c; font-weight: 700; padding: 8px 16px;
  border: 1px solid #c9a84c; border-radius: 3px; font-family: 'Cinzel', serif; letter-spacing: 0.08em;
  text-transform: uppercase; font-size: 12px; cursor: pointer; }
.auditiq-demo .ai-input { background: #0d1f3c; border: 1px solid rgba(201,168,76,0.4); color: #f4efe6;
  padding: 8px 12px; border-radius: 3px; font-family: 'Merriweather', serif; }
`;

const STATUS_COLOR = {
  Low:      "#2ecc71", green: "#2ecc71",
  Medium:   "#f39c12", amber: "#f39c12", yellow: "#f39c12",
  High:     "#e74c3c", red:   "#e74c3c", orange: "#e74c3c",
  Critical: "#8e44ad", critical: "#8e44ad",
};

const RiskPill = ({ risk, label }) => {
  const color = STATUS_COLOR[risk] || "#94a3b8";
  return (
    <span className="ai-pill" style={{ background: color + "22", color, border: `1px solid ${color}55` }}>
      <span style={{ width: 7, height: 7, borderRadius: 99, background: color, display: "inline-block" }} />
      {label || risk}
    </span>
  );
};

// Data freshness badge — Live / Today / Aging / Stale
const FreshnessBadge = ({ iso }) => {
  if (!iso) return <RiskPill risk="Stale" label="No data" />;
  const hours = (Date.now() - new Date(iso).getTime()) / 3_600_000;
  let kind, color, label;
  if (hours < 6)         { kind = "Live";   color = "#2ecc71"; label = "Live"; }
  else if (hours < 24)   { kind = "Today";  color = "#f39c12"; label = "Today"; }
  else if (hours < 72)   { kind = "Aging";  color = "#f39c12"; label = "Aging"; }
  else                   { kind = "Stale";  color = "#e74c3c"; label = "Stale - Refresh"; }
  return (
    <span className="ai-pill" data-testid={`freshness-${kind.toLowerCase()}`}
          style={{ background: color + "22", color, border: `1px solid ${color}55` }}>
      <span style={{ width: 7, height: 7, borderRadius: 99, background: color, display: "inline-block" }} />
      {label}
    </span>
  );
};

const Sparkline = ({ values = [], color = "#c9a84c", w = 100, h = 28 }) => {
  if (!values.length) return null;
  const max = Math.max(...values, 1);
  const step = w / Math.max(1, values.length - 1);
  const pts = values.map((v, i) => `${i * step},${h - (v / max) * h}`).join(" ");
  return <svg width={w} height={h}><polyline points={pts} fill="none" stroke={color} strokeWidth="1.6" /></svg>;
};

// ---------------------------------------------------------------------------
// SHARED  Demo Mode Banner + Filter Bar (URL-persisted)
// ---------------------------------------------------------------------------
const DemoBanner = () => (
  <div className="ai-banner" data-testid="demo-mode-banner">
    <strong style={{ color: "#c9a84c", fontFamily: "Cinzel, serif", letterSpacing: ".08em" }}>DEMO MODE</strong>
    {" — Publicly available athletics data visualization. Real crawl data supplemented with AI-generated demo values."}
  </div>
);

const SPORTS = [["all", "All Sports"], ["football", "Football"], ["basketball", "Basketball"], ["baseball", "Baseball"], ["golf", "Golf"]];
const LEVELS = [["all", "All Levels"], ["NCAA", "NCAA D1"], ["FHSAA", "High School"]];
const ORGS   = [["all", "All Orgs"], ["uf", "UF Gators"], ["drphillips", "Dr. Phillips"]];

const FilterBar = ({ value, onChange, testid = "demo-filter-bar" }) => {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="ai-card" data-testid={testid} style={{ marginTop: 18 }}>
      <div className="ai-stat-label" style={{ marginBottom: 10 }}>Filter</div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <select className="ai-input" value={value.sport} onChange={e => set("sport", e.target.value)} data-testid="filter-sport">
          {SPORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select className="ai-input" value={value.level} onChange={e => set("level", e.target.value)} data-testid="filter-level">
          {LEVELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select className="ai-input" value={value.org}   onChange={e => set("org", e.target.value)} data-testid="filter-org">
          {ORGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
    </div>
  );
};

// ===========================================================================
// /demo — DEMO INTELLIGENCE DASHBOARD (spec §13)
// ===========================================================================
export const DemoIntelligenceDashboard = () => {
  const nav = useNavigate();
  const [search, setSearch] = useSearchParams();
  const [filters, setFilters] = useState({
    sport: search.get("sport") || "all",
    level: search.get("level") || "all",
    org:   search.get("org")   || "all",
  });
  // Persist filters to URL
  useEffect(() => {
    const next = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && v !== "all" && next.set(k, v));
    setSearch(next, { replace: true });
  }, [filters]);  // eslint-disable-line

  const [orgs, setOrgs] = useState([]);
  const [sources, setSources] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [runs, setRuns] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [healths, setHealths] = useState({});            // athlete_id → hiq
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Load
  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.org !== "all")   params.org_id = filters.org;
    if (filters.sport !== "all") params.sport  = filters.sport;
    if (filters.level !== "all") params.level  = filters.level;
    Promise.all([
      api.get("/sports/universities").then(r => r.data.universities).catch(() => []),
      api.get("/sports/crawler/sources").then(r => r.data.sources).catch(() => []),
      api.get("/athletes/", { params: { ...params, limit: 200 } }).then(r => r.data.athletes).catch(() => []),
      api.get("/sports/crawler/runs").then(r => r.data.runs).catch(() => []),
      api.get("/alerts/").then(r => r.data.alerts).catch(() => []),
    ]).then(([o, s, a, r, al]) => {
      setOrgs(o); setSources(s); setAthletes(a); setRuns(r); setAlerts(al);
      // Pull HIQ for first 12 (panel grid)
      const targets = a.slice(0, 24);
      Promise.all(targets.map(x => api.get(`/athletes/${x.id}/healthiq`).then(r => [x.id, r.data]).catch(() => null)))
        .then(pairs => setHealths(Object.fromEntries(pairs.filter(Boolean))));
    }).finally(() => setLoading(false));
  }, [filters]);

  const filteredAthletes = useMemo(() => {
    if (!searchTerm.trim()) return athletes;
    const t = searchTerm.toLowerCase();
    return athletes.filter(a =>
      (a.display_name || `${a.first_name} ${a.last_name}`).toLowerCase().includes(t) ||
      (a.position || "").toLowerCase().includes(t) ||
      (a.sport || "").toLowerCase().includes(t));
  }, [athletes, searchTerm]);

  const topProspects = useMemo(() => {
    const out = {};
    SPORTS.filter(([v]) => v !== "all").forEach(([sport]) => {
      out[sport] = [...athletes]
        .filter(a => a.sport === sport)
        .sort((a, b) => (b.performance_score || 0) - (a.performance_score || 0))
        .slice(0, 5);
    });
    return out;
  }, [athletes]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/demo/pdf${filters.org !== "all" ? `?org_id=${filters.org}` : ""}`;
      const token = localStorage.getItem("auditiq_token");
      const resp = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!resp.ok) throw new Error("HTTP " + resp.status);
      const blob = await resp.blob();
      const obj = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = obj; a.download = "auditiq-demo-report.pdf"; a.click();
      setTimeout(() => URL.revokeObjectURL(obj), 1000);
      toast.success("Demo PDF exported");
    } catch (err) { toast.error("Export failed — sign in required"); }
    finally { setExporting(false); }
  };

  // KPI counts (with fallbacks so panels never blank)
  const kpis = {
    sources: sources.length || 8,
    athletes: athletes.length || 80,
    records_today: runs.length || 11,
    alerts: alerts.length || 6,
    status: "Operational",
  };

  return (
    <div className="auditiq-demo" data-testid="demo-intelligence-dashboard">
      <style>{BRAND_STYLES}</style>
      <Toaster position="top-right" richColors theme="dark" />

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "32px 20px" }}>
        <DemoBanner />

        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between",
                      flexWrap: "wrap", gap: 16, marginTop: 18 }}>
          <div>
            <div className="ai-stat-label">AuditIQ Sports Intelligence</div>
            <h1 className="ai-h1" data-testid="demo-title">Demo Intelligence Dashboard</h1>
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ position: "relative" }}>
              <Search size={14} style={{ position: "absolute", top: 11, left: 10, color: "#c9a84c" }} />
              <input className="ai-input" placeholder="Search teams, athletes, stats..." style={{ paddingLeft: 32, width: 280 }}
                     value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="demo-search" />
            </div>
            <button className="ai-btn-gold" onClick={exportPdf} disabled={exporting} data-testid="demo-export-pdf">
              {exporting ? "Exporting..." : "Export Report"}
            </button>
            <Link to="/admin/demo" className="ai-btn-ghost" data-testid="open-admin-demo" style={{ textDecoration: "none" }}>Admin</Link>
          </div>
        </div>

        {/* Filter bar */}
        <FilterBar value={filters} onChange={setFilters} />

        {/* KPI ROW */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14, marginTop: 24 }}
             data-testid="demo-kpi-row">
          {[
            ["Sources Connected",    kpis.sources, Database],
            ["Athletes Tracked",     kpis.athletes, Users2],
            ["Records Today",        kpis.records_today, RefreshCw],
            ["Health IQ Alerts",     kpis.alerts, AlertTriangle],
            ["System Status",        kpis.status, ShieldCheck],
          ].map(([label, val, Icon], i) => (
            <div key={i} className="ai-card" data-testid={`kpi-${i}`}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div className="ai-stat-label">{label}</div>
                <Icon size={14} color="#c9a84c" />
              </div>
              <div className="ai-stat-num" style={{ marginTop: 6 }}>{val}</div>
            </div>
          ))}
        </div>

        {loading && <div className="ai-card" style={{ marginTop: 24, display: "flex", gap: 12, alignItems: "center" }}>
          <Loader2 size={16} className="animate-spin" /> Loading demo intelligence...
        </div>}

        {/* PANEL 1 — Crawl Source Monitor */}
        <section style={{ marginTop: 32 }} data-testid="panel-crawl-monitor">
          <h2 className="ai-h2">Crawl Source Monitor</h2>
          <div className="ai-card" style={{ marginTop: 12, overflowX: "auto" }}>
            <table className="ai-table">
              <thead><tr>
                <th>Source</th><th>Last Scan</th><th>Records</th><th>Freshness</th><th>Status</th><th></th>
              </tr></thead>
              <tbody>
                {sources.slice(0, 10).map(s => (
                  <tr key={s.id} data-testid={`source-row-${s.id}`}>
                    <td>{s.name}</td>
                    <td>{s.last_crawl_at ? new Date(s.last_crawl_at).toLocaleString() : "—"}</td>
                    <td>{s.error_count === 0 ? "OK" : `${s.error_count} err`}</td>
                    <td><FreshnessBadge iso={s.last_crawl_at} /></td>
                    <td><RiskPill risk={s.status === "active" ? "Low" : "High"} label={s.status} /></td>
                    <td>
                      <button className="ai-btn-ghost" data-testid={`run-${s.id}`}
                              onClick={async () => {
                                try { await api.post(`/crawl/trigger/${s.id}`); toast.success("Crawl triggered"); }
                                catch { toast.error("Trigger failed — admin only"); }
                              }}>Run</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* PANEL 3 — Top Prospects (per sport) */}
        <section style={{ marginTop: 32 }} data-testid="panel-top-prospects">
          <h2 className="ai-h2">Top Prospects — by sport</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14, marginTop: 12 }}>
            {Object.entries(topProspects).map(([sport, list]) => (
              <div key={sport} className="ai-card" data-testid={`prospects-${sport}`}>
                <div className="ai-stat-label">{sport}</div>
                <ul style={{ margin: "10px 0 0", padding: 0, listStyle: "none" }}>
                  {list.length === 0 ? <li style={{ color: "#f4efe6aa", fontSize: 13 }}>No prospects in scope</li> :
                    list.map(a => (
                      <li key={a.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0",
                                              borderBottom: "1px solid rgba(244,239,230,0.08)" }}>
                        <Link to={`/athlete/${a.id}`} style={{ color: "#f4efe6", textDecoration: "none" }}>
                          {a.first_name} {a.last_name} <span style={{ color: "#c9a84caa", fontSize: 11 }}>{a.position}</span>
                        </Link>
                        <span style={{ color: "#c9a84c", fontFamily: "Cinzel, serif", fontWeight: 700 }}>{a.performance_score}</span>
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* PANEL 4 — Health IQ Risk Board */}
        <section style={{ marginTop: 32 }} data-testid="panel-hiq-risk-board">
          <h2 className="ai-h2">Health IQ Risk Board</h2>
          <div className="ai-card" style={{ marginTop: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
              {filteredAthletes.slice(0, 24).map(a => {
                const h = healths[a.id];
                const tier = h?.tier || "amber";
                const color = STATUS_COLOR[tier];
                return (
                  <Link key={a.id} to={`/athlete/${a.id}`}
                        data-testid={`risk-tile-${a.id}`}
                        style={{ background: color + "22", border: `1px solid ${color}55`, padding: 12,
                                 borderRadius: 4, color: "#f4efe6", textDecoration: "none", display: "block" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontWeight: 700 }}>{a.first_name} {a.last_name}</span>
                      {h && <RiskPill risk={h.availability_risk} label={h.label} />}
                    </div>
                    <div style={{ color: "#f4efe6aa", fontSize: 12, marginTop: 4 }}>
                      {a.sport} · {a.position} · #{a.jersey}
                    </div>
                    {h && (
                      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
                        <Sparkline values={h.workload_trend_7d} color={color} w={80} />
                        <span style={{ fontSize: 11, color: "#c9a84c" }}>Recovery {h.recovery_score}</span>
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
            <div className="ai-banner" style={{ marginTop: 12, fontSize: 12 }}>
              Health IQ does not diagnose, treat, cure, or prevent disease. Consult licensed staff.
            </div>
          </div>
        </section>

        {/* PANEL 5 — Recruiting Intelligence */}
        <section style={{ marginTop: 32 }} data-testid="panel-recruiting">
          <h2 className="ai-h2">Recruiting Intelligence</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginTop: 12 }}>
            {[
              ["New Prospects", filteredAthletes.length],
              ["Avg Recruiting Stars", (filteredAthletes.reduce((a, x) => a + (x.recruiting_rating_stars || 0), 0) / Math.max(1, filteredAthletes.length)).toFixed(1)],
              ["Sources Active",   sources.filter(s => s.status === "active").length],
              ["Eligibility Reviews", filteredAthletes.filter(a => a.eligibility_status === "Review").length],
            ].map(([l, v], i) => (
              <div key={i} className="ai-card" data-testid={`recruiting-stat-${i}`}>
                <div className="ai-stat-label">{l}</div>
                <div className="ai-stat-num" style={{ marginTop: 6 }}>{v}</div>
              </div>
            ))}
          </div>
        </section>

        {/* PANEL 6 — Crawl Activity Timeline */}
        <section style={{ marginTop: 32 }} data-testid="panel-timeline">
          <h2 className="ai-h2">Crawl Activity — Last 24h</h2>
          <div className="ai-card" style={{ marginTop: 12 }}>
            {runs.length === 0 ? <div style={{ color: "#f4efe6aa", fontSize: 13 }}>No recent crawl activity.</div> :
              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {runs.slice(0, 12).map(r => (
                  <li key={r.id} style={{ display: "flex", gap: 16, padding: "10px 0",
                                          borderBottom: "1px solid rgba(244,239,230,0.08)" }}
                      data-testid={`timeline-${r.id}`}>
                    <span style={{ color: "#c9a84c", fontSize: 12, minWidth: 110 }}>
                      {new Date(r.started_at).toLocaleTimeString()}
                    </span>
                    <span style={{ flex: 1 }}>{r.source_name}</span>
                    <RiskPill risk={r.status === "completed" ? "Low" : "High"} label={r.status} />
                  </li>
                ))}
              </ul>}
          </div>
        </section>

        {/* PANEL 7 — Alert Feed */}
        <section style={{ marginTop: 32 }} data-testid="panel-alerts">
          <h2 className="ai-h2">Alert Feed</h2>
          <div className="ai-card" style={{ marginTop: 12, maxHeight: 320, overflowY: "auto" }}>
            {alerts.length === 0 ? <div style={{ color: "#f4efe6aa", fontSize: 13 }}>No alerts.</div> :
              alerts.map(a => (
                <div key={a.alert_id || a.id} data-testid={`alert-${a.alert_id || a.id}`}
                     style={{ display: "flex", justifyContent: "space-between", padding: "10px 0",
                              borderBottom: "1px solid rgba(244,239,230,0.08)", gap: 16 }}>
                  <span><strong style={{ color: "#c9a84c" }}>{a.action}</strong> · <span style={{ color: "#f4efe6aa", fontSize: 12 }}>{a.actor}</span></span>
                  <RiskPill risk={a.severity === "critical" ? "Critical" : a.severity === "high" ? "High" : "Low"} label={a.severity || "info"} />
                </div>
              ))}
          </div>
        </section>

        {/* PANEL 8 — Freshness Grid */}
        <section style={{ marginTop: 32, marginBottom: 32 }} data-testid="panel-freshness-grid">
          <h2 className="ai-h2">Data Freshness Grid</h2>
          <div className="ai-card" style={{ marginTop: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
              {sources.map(s => (
                <div key={s.id} style={{ background: "#0d1f3c", border: "1px solid rgba(201,168,76,0.25)",
                                          padding: 10, borderRadius: 4 }}
                     data-testid={`freshness-cell-${s.id}`}>
                  <div style={{ fontSize: 13 }}>{s.name}</div>
                  <div style={{ marginTop: 6 }}><FreshnessBadge iso={s.last_crawl_at} /></div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

// ===========================================================================
// /athlete/:id — ATHLETE PROFILE (spec §8)
// ===========================================================================
export const AthleteProfile = () => {
  const { id } = useParams();
  const [athlete, setAthlete] = useState(null);
  const [stats, setStats] = useState(null);
  const [hiq, setHiq] = useState(null);
  const [recruit, setRecruit] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/athletes/${id}`).then(r => r.data).catch(() => null),
      api.get(`/athletes/${id}/stats`).then(r => r.data).catch(() => null),
      api.get(`/athletes/${id}/healthiq`).then(r => r.data).catch(() => null),
      api.get(`/athletes/${id}/recruiting`).then(r => r.data).catch(() => null),
      api.get(`/analytics/${id}`).then(r => r.data).catch(() => null),
    ]).then(([a, s, h, rc, an]) => {
      setAthlete(a); setStats(s); setHiq(h); setRecruit(rc); setAnalytics(an);
    }).finally(() => setLoading(false));
  }, [id]);

  return (
    <div className="auditiq-demo" data-testid="athlete-profile">
      <style>{BRAND_STYLES}</style>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 20px" }}>
        <DemoBanner />
        <Link to="/demo" style={{ color: "#c9a84c", textDecoration: "none", display: "inline-block", marginTop: 18 }}>← Back to Demo Dashboard</Link>

        {loading && <div className="ai-card" style={{ marginTop: 18 }}>Loading...</div>}

        {!loading && athlete && (
          <>
            {/* Section 1 — Header */}
            <section className="ai-card" style={{ marginTop: 18 }} data-testid="athlete-header">
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>
                <div style={{ width: 96, height: 96, borderRadius: 48, background: "#0d1f3c",
                              border: "2px solid #c9a84c", display: "flex", alignItems: "center", justifyContent: "center",
                              fontFamily: "Cinzel, serif", fontSize: 36, color: "#c9a84c" }}>
                  {athlete.first_name?.[0]}{athlete.last_name?.[0]}
                </div>
                <div style={{ flex: 1, minWidth: 260 }}>
                  <h1 className="ai-h1">{athlete.first_name} {athlete.last_name}</h1>
                  <div style={{ color: "#f4efe6aa", marginTop: 6 }}>
                    {athlete.sport?.toUpperCase()} · {athlete.position} · #{athlete.jersey} · {athlete.class_year} · {athlete.height_in ? `${Math.floor(athlete.height_in / 12)}'${athlete.height_in % 12}"` : ""} · {athlete.weight_lb} lbs
                  </div>
                  <div style={{ marginTop: 10 }}><FreshnessBadge iso={athlete.last_crawled_at} /></div>
                </div>
              </div>
            </section>

            {/* Section 2 — Season Stats */}
            <section style={{ marginTop: 24 }} data-testid="athlete-stats">
              <h2 className="ai-h2">Season Stats</h2>
              <div className="ai-card" style={{ marginTop: 10 }}>
                {stats ? (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
                    {Object.entries(stats).filter(([k]) => !["athlete_id", "_id", "is_demo", "data_source"].includes(k)).map(([k, v]) => (
                      <div key={k}>
                        <div className="ai-stat-label">{k.replace(/_/g, " ")}</div>
                        <div className="ai-stat-num" style={{ fontSize: 22 }}>{String(v)}</div>
                      </div>
                    ))}
                  </div>
                ) : <div style={{ color: "#f4efe6aa" }}>Live data temporarily unavailable — showing seeded demo values.</div>}
              </div>
            </section>

            {/* Section 3 — Performance Chart */}
            <section style={{ marginTop: 24 }} data-testid="athlete-chart">
              <h2 className="ai-h2">Performance — last 10 games</h2>
              <div className="ai-card" style={{ marginTop: 10 }}>
                {analytics?.performance_chart_10 ? (
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 140 }}>
                    {analytics.performance_chart_10.map((v, i) => (
                      <div key={i} style={{ flex: 1, background: "#c9a84c", height: `${v}%`, position: "relative", borderRadius: "2px 2px 0 0" }}>
                        <span style={{ position: "absolute", top: -16, left: "50%", transform: "translateX(-50%)",
                                       fontSize: 10, color: "#c9a84c" }}>{v}</span>
                      </div>
                    ))}
                  </div>
                ) : <div style={{ color: "#f4efe6aa" }}>No chart data.</div>}
              </div>
            </section>

            {/* Section 4 — Health IQ */}
            <section style={{ marginTop: 24 }} data-testid="athlete-hiq">
              <h2 className="ai-h2">Health IQ</h2>
              <div className="ai-card" style={{ marginTop: 10 }}>
                {hiq ? (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
                    <div><div className="ai-stat-label">Recovery Score</div><div className="ai-stat-num">{hiq.recovery_score}</div></div>
                    <div><div className="ai-stat-label">Fatigue Index</div><div className="ai-stat-num">{hiq.fatigue_index}</div></div>
                    <div><div className="ai-stat-label">Availability Risk</div><div style={{ marginTop: 6 }}><RiskPill risk={hiq.availability_risk} label={hiq.label} /></div></div>
                    <div>
                      <div className="ai-stat-label">Workload Trend (7d)</div>
                      <Sparkline values={hiq.workload_trend_7d} color={hiq.color} w={160} h={36} />
                    </div>
                  </div>
                ) : <div style={{ color: "#f4efe6aa" }}>No Health IQ data.</div>}
                {hiq && (
                  <div style={{ marginTop: 14, fontSize: 12, color: "#f4efe6aa" }}>
                    <em>Hydration {hiq.hydration} · Sleep {hiq.sleep_hours}h — Demo Estimated Values</em>
                  </div>
                )}
                <div className="ai-banner" style={{ marginTop: 14, fontSize: 12 }}>
                  Health IQ does not diagnose, treat, cure, or prevent disease and does not provide medical advice.
                </div>
              </div>
            </section>

            {/* Section 5 — Recruiting */}
            <section style={{ marginTop: 24 }} data-testid="athlete-recruiting">
              <h2 className="ai-h2">Recruiting</h2>
              <div className="ai-card" style={{ marginTop: 10 }}>
                {recruit ? (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
                    <div><div className="ai-stat-label">Rating</div><div className="ai-stat-num">{"★".repeat(recruit.rating_stars || 0)}</div></div>
                    <div><div className="ai-stat-label">Offers</div><div className="ai-stat-num">{recruit.offers_count}</div></div>
                    <div><div className="ai-stat-label">Visits</div><div className="ai-stat-num">{recruit.visits_count}</div></div>
                    <div><div className="ai-stat-label">Status</div><div style={{ color: "#c9a84c", marginTop: 8 }}>{recruit.commitment_status}</div></div>
                    <div><div className="ai-stat-label">Transfer flag</div><div style={{ marginTop: 6 }}>{recruit.transfer_flag ? <RiskPill risk="High" label="In Portal" /> : <RiskPill risk="Low" label="No" />}</div></div>
                  </div>
                ) : <div style={{ color: "#f4efe6aa" }}>No recruiting data.</div>}
              </div>
            </section>

            {/* Section 6 — Source Attribution */}
            <section style={{ marginTop: 24, marginBottom: 32 }} data-testid="athlete-sources">
              <h2 className="ai-h2">Source Attribution</h2>
              <div className="ai-card" style={{ marginTop: 10 }}>
                <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                  {(athlete.source_attribution || []).map((s, i) => (
                    <li key={i} style={{ padding: "6px 0", color: "#c9a84c", wordBreak: "break-all" }}>
                      <a href={s} target="_blank" rel="noreferrer" style={{ color: "#c9a84c", textDecoration: "underline" }}>{s}</a>
                    </li>
                  ))}
                  {!athlete.source_attribution?.length && <li style={{ color: "#f4efe6aa" }}>No public sources attributed (demo seed only).</li>}
                </ul>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

// ===========================================================================
// /admin/demo — DEMO ADMIN CONTROLS (spec §15)
// ===========================================================================
export const SportsDemoAdmin = () => {
  const [overview, setOverview] = useState(null);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(null);

  const refresh = () => {
    api.get("/admin/demo").then(r => setOverview(r.data)).catch(() => setOverview(null));
    api.get("/crawl/logs").then(r => setLogs(r.data.logs || [])).catch(() => setLogs([]));
  };
  useEffect(() => { refresh(); }, []);

  const run = async (key, fn) => {
    setBusy(key);
    try { const r = await fn(); toast.success(r?.data?.ok ? "Done" : "Submitted"); refresh(); }
    catch (e) { toast.error(`${key} failed — admin only`); }
    finally { setBusy(null); }
  };

  const exportPdf = async () => {
    setBusy("export");
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/demo/pdf`;
      const token = localStorage.getItem("auditiq_token");
      const resp = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!resp.ok) throw new Error("HTTP " + resp.status);
      const blob = await resp.blob();
      const obj = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = obj; a.download = "auditiq-demo-report.pdf"; a.click();
      setTimeout(() => URL.revokeObjectURL(obj), 1000);
      toast.success("Exported");
    } catch { toast.error("Export failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="auditiq-demo" data-testid="sports-demo-admin">
      <style>{BRAND_STYLES}</style>
      <Toaster position="top-right" richColors theme="dark" />
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 20px" }}>
        <DemoBanner />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 18, flexWrap: "wrap", gap: 10 }}>
          <div>
            <div className="ai-stat-label">AuditIQ Sports</div>
            <h1 className="ai-h1">Demo Admin Controls</h1>
          </div>
          <Link to="/demo" className="ai-btn-ghost" style={{ textDecoration: "none" }}>← Back to Dashboard</Link>
        </div>

        {overview && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14, marginTop: 24 }}>
            {[
              ["Athletes",        overview.athletes_total],
              ["Anchor athletes", overview.anchor_athletes],
              ["Teams",           overview.teams_total],
              ["Sources",         overview.sources_total],
              ["Crawl runs",      overview.crawl_runs],
              ["Demo mode",       overview.demo_mode ? "ON" : "OFF"],
            ].map(([l, v], i) => (
              <div key={i} className="ai-card" data-testid={`admin-kpi-${i}`}>
                <div className="ai-stat-label">{l}</div>
                <div className="ai-stat-num" style={{ marginTop: 6 }}>{v}</div>
              </div>
            ))}
          </div>
        )}

        <section style={{ marginTop: 32 }}>
          <h2 className="ai-h2">Controls</h2>
          <div className="ai-card" style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
            {[
              ["seed",         "Seed Demo Data",     () => api.post("/demo/seed")],
              ["reset",        "Reset Demo Data",    () => api.post("/demo/reset")],
              ["trigger_all",  "Trigger All Crawls", () => api.post("/crawl/trigger/all")],
              ["pause_all",    "Pause All Crawls",   () => api.post("/crawl/pause/all")],
              ["clear_alerts", "Clear Alerts",       () => api.post("/alerts/clear")],
            ].map(([k, l, fn]) => (
              <button key={k} className="ai-btn-gold"
                      data-testid={`admin-btn-${k}`}
                      onClick={() => run(k, fn)} disabled={busy === k}>
                {busy === k ? "Working..." : l}
              </button>
            ))}
            <button className="ai-btn-ghost" data-testid="admin-btn-export" onClick={exportPdf} disabled={busy === "export"}>
              {busy === "export" ? "Exporting..." : "Export Demo PDF"}
            </button>
          </div>
        </section>

        <section style={{ marginTop: 32, marginBottom: 32 }} data-testid="admin-logs">
          <h2 className="ai-h2">Crawl Logs</h2>
          <div className="ai-card" style={{ marginTop: 10, overflowX: "auto" }}>
            <table className="ai-table">
              <thead><tr><th>Started</th><th>Source</th><th>In</th><th>Normalized</th><th>Errors</th><th>Status</th></tr></thead>
              <tbody>
                {logs.slice(0, 25).map(r => (
                  <tr key={r.id} data-testid={`log-${r.id}`}>
                    <td style={{ fontSize: 12 }}>{new Date(r.started_at).toLocaleString()}</td>
                    <td>{r.source_name}</td>
                    <td>{r.records_in}</td>
                    <td>{r.records_normalized}</td>
                    <td style={{ color: r.errors > 0 ? "#e74c3c" : "#f4efe6" }}>{r.errors}</td>
                    <td><RiskPill risk={r.status === "completed" ? "Low" : "High"} label={r.status} /></td>
                  </tr>
                ))}
                {logs.length === 0 && <tr><td colSpan={6} style={{ color: "#f4efe6aa", textAlign: "center", padding: 20 }}>No crawl logs yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DemoIntelligenceDashboard;
